import { Link } from "react-router-dom";
import { ArrowRight, Camera, CheckCircle2, FileSpreadsheet, LockKeyhole, MessageCircle, Mic, ShieldCheck, Users, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";

const features = [
  { icon: Wallet, title: "Registros simples", text: "Anote receitas, despesas e transferências em poucos toques — muito mais claro que uma planilha." },
  { icon: Mic, title: "Por voz", text: "Fale o lançamento, confira os dados e salve. Você continua no controle antes de confirmar." },
  { icon: Camera, title: "Com foto", text: "Fotografe um comprovante para preencher valor, data e estabelecimento com revisão antes do registro." },
  { icon: Users, title: "Para sua rotina", text: "Acompanhe seus registros de forma simples e organizada." },
  { icon: FileSpreadsheet, title: "Relatórios exportáveis", text: "Veja seus resultados e leve os dados para o Excel quando precisar." },
  { icon: MessageCircle, title: "WhatsApp em evolução", text: "O registro por mensagens está sendo preparado para o número oficial do Bolso." },
];

export default function Landing() {
  return (
    <main className="min-h-screen bg-[#f8f8f3] text-[#17382b]">
      <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5 sm:px-8">
        <Link to="/" className="flex items-center gap-3 font-serif text-2xl font-semibold text-[#064c2e]" aria-label="Bolso, página inicial">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#006b3f] text-white shadow-sm"><Wallet className="h-5 w-5" /></span>
          Bolso
        </Link>
        <Button asChild variant="outline" className="border-[#006b3f]/25 bg-white text-[#064c2e] hover:bg-[#e8f3eb]">
          <Link to="/login">Entrar</Link>
        </Button>
      </header>

      <section className="mx-auto grid max-w-6xl gap-10 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-[1.1fr_.9fr] lg:items-center lg:pb-28 lg:pt-20">
        <div>
          <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#006b3f]/15 bg-white px-3 py-1.5 text-sm font-medium text-[#176441]">
            <CheckCircle2 className="h-4 w-4" /> Organização financeira feita para a vida real
          </p>
          <h1 className="max-w-3xl font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-[#12392a] sm:text-6xl">
            Menos planilhas. Mais clareza para sua vida financeira.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-[#476257]">
            O Bolso ajuda você a registrar e acompanhar o dinheiro que entra e sai — pelo celular, por voz ou com foto de comprovante. Entre rapidamente com sua conta Google. Sem integração com bancos e sem movimentar sua conta.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild size="lg" className="bg-[#006b3f] px-6 text-base hover:bg-[#005532]">
              <Link to="/login">Começar a organizar <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
            <a href="#como-funciona" className="inline-flex h-11 items-center justify-center rounded-md px-5 text-sm font-medium text-[#176441] underline-offset-4 hover:underline">
              Conhecer o Bolso
            </a>
          </div>
          <div className="mt-8 flex items-start gap-3 text-sm text-[#476257]">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#176441]" />
            <span>Você registra os dados que quiser. O Bolso não pede senha bancária, não acessa seu banco e não realiza pagamentos.</span>
          </div>
        </div>

        <div className="rounded-[2rem] border border-[#dce9df] bg-white p-5 shadow-[0_24px_60px_rgba(17,67,40,.12)] sm:p-7">
          <div className="rounded-2xl bg-[#e9f5ec] p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-[#47705a]">Visão do mês</p>
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#176441]">Exemplo</span>
            </div>
            <p className="mt-2 font-serif text-3xl font-semibold text-[#0b5635]">R$ 1.280,00</p>
            <p className="mt-1 text-sm text-[#47705a]">Saldo registrado</p>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[#f0ddd8] bg-[#fff9f7] p-4"><p className="text-xs text-[#8b655d]">Despesas</p><p className="mt-1 font-semibold text-[#c53f2f]">R$ 820,00</p></div>
            <div className="rounded-xl border border-[#d8ecdd] bg-[#fbfefb] p-4"><p className="text-xs text-[#47705a]">Receitas</p><p className="mt-1 font-semibold text-[#107a48]">R$ 2.100,00</p></div>
          </div>
          <div className="mt-4 rounded-xl border border-[#e2ebe3] p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-[#6a8173]">Próximo lançamento</p>
            <div className="mt-2 flex items-center justify-between gap-3"><span className="text-sm font-medium">Mercado da semana</span><span className="font-semibold text-[#c53f2f]">− R$ 186,40</span></div>
          </div>
        </div>
      </section>

      <section id="como-funciona" className="border-y border-[#dfe9e1] bg-white/70 py-20">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <div className="max-w-2xl"><p className="text-sm font-semibold uppercase tracking-[.16em] text-[#176441]">Do seu jeito</p><h2 className="mt-3 font-serif text-3xl font-semibold text-[#12392a] sm:text-4xl">Um caminho simples do registro à decisão.</h2></div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {features.map(({ icon: Icon, title, text }) => <article key={title} className="rounded-2xl border border-[#e1ebe3] bg-white p-5 shadow-sm"><span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#e8f3eb] text-[#07663d]"><Icon className="h-5 w-5" /></span><h3 className="mt-4 text-base font-semibold text-[#17382b]">{title}</h3><p className="mt-2 text-sm leading-relaxed text-[#5b7165]">{text}</p></article>)}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-20 text-center sm:px-8">
        <h2 className="font-serif text-3xl font-semibold text-[#12392a] sm:text-4xl">Comece pelo primeiro lançamento.</h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-[#536b5e]">Organize uma informação de cada vez. Você pode começar gratuitamente pelo link e entrar com sua conta Google, sem convite prévio.</p>
        <Button asChild size="lg" className="mt-7 bg-[#006b3f] text-base hover:bg-[#005532]"><Link to="/login">Criar ou acessar minha conta <ArrowRight className="ml-2 h-4 w-4" /></Link></Button>
      </section>

      <footer className="border-t border-[#dfe9e1] px-5 py-7 text-center text-sm text-[#60776a]"><p>Bolso · Controle financeiro pessoal</p><p className="mt-2"><Link className="underline underline-offset-2 hover:text-[#17382b]" to="/termos-de-uso">Termos de Uso</Link><span className="mx-2">·</span><Link className="underline underline-offset-2 hover:text-[#17382b]" to="/politica-de-privacidade">Política de Privacidade</Link></p></footer>
    </main>
  );
}
